import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { NakedButton, styles as dsStyles } from '@frontend/design-system';
import { useDeepLinkedEvent } from '../../hooks/calendar/use-deep-linked-event';
import { useSetCalendarAppointmentListAsSelected } from '../../stores/schedule-appointment-info-panel.store';
import { getMinDate } from '../../utils/date-helpers';
import { OVERLAPPING_EVENTS_BUTTON_WIDTH } from './constants';
import { useCalendarViewProps } from './providers';
import { ProcessedEventData } from './types';
import { createGridColumnsFromStartAndEndTimestamp, zIndex } from './utils';
import { useZoomSafeContext } from './zoom/zoom-context';

type OverlappingEventsProps = {
  overlappingEvents: ProcessedEventData[];
  visibleLaneCount: number;
};

const useSetOverlappingEventButtonSelection = (
  hiddenEvents: OverlappingEventsProps['overlappingEvents'],
  calendarDate: string
): { isSelected: boolean; onSelect: MouseEventHandler<HTMLButtonElement> } => {
  const hiddenEventIds = useMemo(() => hiddenEvents.map((event) => event.eventId), [hiddenEvents]);

  const { isSelected: isOverlappingApptSelected, setAppointments: setOverlappingAppointments } =
    useSetCalendarAppointmentListAsSelected(hiddenEventIds, calendarDate);

  const onSelect = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      setOverlappingAppointments();
    },
    [setOverlappingAppointments]
  );

  return {
    isSelected: isOverlappingApptSelected,
    onSelect,
  };
};

export const OverlappingEvents = ({ visibleLaneCount, overlappingEvents }: OverlappingEventsProps) => {
  const [visible, setVisible] = useState(false);
  const { pixelsPerMinute } = useZoomSafeContext();
  const { startHour: calendarStartHour, endHour: calendarEndHour, calendarDate } = useCalendarViewProps();
  const eventButtonRef = useRef<HTMLButtonElement | null>(null);

  const hiddenEvents = useMemo(
    () => overlappingEvents.filter((event) => event.laneIndex + 1 > visibleLaneCount),
    [overlappingEvents, visibleLaneCount]
  );
  const hiddenEventIds = useMemo(() => hiddenEvents.map((event) => event.eventId), [hiddenEvents]);

  const { isSelected, onSelect } = useSetOverlappingEventButtonSelection(hiddenEvents, calendarDate);

  const minStartTime = useMemo(() => {
    if (hiddenEvents.length === 0) return null;

    // for yesterday event which continue on calendar selected date, we need to set minStartTime to the start of the calendar date
    if (
      hiddenEvents.some(
        (event) => event.startDate !== event.endDate && dayjs(event.startDate).isBefore(dayjs(calendarDate))
      )
    ) {
      return dayjs(calendarDate).startOf('day');
    }

    return getMinDate(hiddenEvents.map((event) => dayjs(event.startHour, 'hh:mm A')));
  }, [calendarDate, hiddenEvents]);

  const { isDeepLinkedEvent } = useDeepLinkedEvent({
    selectedEventIds: hiddenEventIds,
    eventCardRef: eventButtonRef,
  });

  useEffect(() => {
    setVisible(!!hiddenEvents.length);
  }, [hiddenEvents]);

  if (!minStartTime || !minStartTime.isValid()) return null;

  const { startGridColumn, eventDuration } = createGridColumnsFromStartAndEndTimestamp(
    calendarStartHour,
    calendarEndHour,
    pixelsPerMinute
  )({
    startHourTimestamp: minStartTime.format('hh:mm A'),
    endHourTimeStamp: dayjs(minStartTime).add(30, 'm').format('hh:mm A'),
  });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0, zIndex: zIndex.eventCardDefault }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          css={buttonContainerStyle(startGridColumn, eventDuration * pixelsPerMinute)}
        >
          <NakedButton
            onClick={onSelect}
            onMouseDown={(e) => e.stopPropagation()}
            css={buttonStyle(isSelected || isDeepLinkedEvent)}
            ref={eventButtonRef}
          >
            +{hiddenEvents.length}
          </NakedButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const buttonContainerStyle = (top: number, height: number) =>
  css({
    top,
    height,
    right: 0,
    position: 'absolute',
    zIndex: zIndex.overlappingEventButton,
    ':hover': {
      zIndex: zIndex.eventCardDefault,
    },
  });

const buttonStyle = (isSelected: boolean) =>
  css([
    dsStyles.flexCenter,
    {
      width: OVERLAPPING_EVENTS_BUTTON_WIDTH,
      height: 40,
      color: theme.colors.neutral90,
      backgroundColor: theme.colors.white,
      borderRadius: 500,
      fontSize: theme.font.size.medium,
      fontWeight: theme.font.weight.bold,
      userSelect: 'none',
      cursor: 'pointer',
      boxShadow: theme.shadows.light,
      transition: 'box-shadow 0.2s ease-in-out, scale 0.2s ease-in-out,outline 0.1s linear',
      ':hover': {
        scale: 1.05,
        boxShadow: theme.shadows.floating,
      },
      outline: isSelected ? `2px solid ${theme.colors.primary50}` : 'none',
      outlineOffset: 2,
    },
  ]);
